
import { useContext } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import { GeneratedTopicModel } from 'interfaces/GeneratedTopicModel';
import { PromptFlowExecutionRequest, ResponseType } from 'interfaces/PromptFlowExecutionRequest';
import { BatchJob } from 'models/BatchJob';
import { Document } from 'models/Document';
import { GeneratedTopic, SearchIntent } from 'models/GeneratedTopic';
import { HttpMethod } from 'models/HttpMethod';
import { LogLevel } from 'models/LogLevel';
import { Project } from 'models/Project';
import { PromptFlowExecution } from 'models/PromptFlow';
import { SiteSection } from 'models/SiteSection';
import { ISeoKeyword, TargetKeyword } from 'models/TargetKeyword';
import { TopicModel } from 'models/TopicModel';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import { buildRequestSettings } from '../functions/buildRequestSettings';

export interface AIGenerator {
  generateContent: (draftDocument: Document, promptFlowId: number) => Promise<PromptFlowExecution>;
  generateArticleThemes: (project: Project, sections: SiteSection[]) => Promise<GeneratedTopic[]>;
  executePromptFlow: (request: PromptFlowExecutionRequest) => Promise<PromptFlowExecution>;
  triggerPromptFlowBatchJob: (request: PromptFlowExecutionRequest) => Promise<BatchJob>;
  checkPromptFlowExecutionStatus: (batchJobId: string) => Promise<BatchJob>;
  getSubdomainsForDomain: (project: Project, requestId?: string) => Promise<TopicModel[]>;
  tryGetKeywordOpportunitiesOrDefault: (project: Project, seedTopics: TopicModel[]) => Promise<TargetKeyword[]>;
  generateArticleIdeas: (project: Project, topicModels: TopicModel[], seedKeywords?: string[], keywordOpportunities?: ISeoKeyword[], requestId?: string) => Promise<GeneratedTopicModel[]>;
}

export const useAIGenerator = (apiBaseUrl: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const { sendLog, notify } = useContext(AppContext);

  const callApi = async <EntityType, ErrorType = EntityType>(apiMethod: string, method: HttpMethod = HttpMethod.POST, body: any = undefined, tryParseError = false): Promise<EntityType | ErrorType> => {
    const token = await getAccessTokenSilently();
    if (token) {
      const apiUrl = `${apiBaseUrl}/api/${apiMethod}`;
      const fetchResponse = await fetch(
        apiUrl,
        buildRequestSettings(method, token, body),
      );
      if (fetchResponse.ok) {
        const result: EntityType = await fetchResponse.json();
        return result;
      }

      if (tryParseError && fetchResponse.body) {
        const error: ErrorType | undefined = await fetchResponse.json();
        if (error) {
          sendLog(`api: ${apiUrl} failed with ${fetchResponse.status}: ${fetchResponse.statusText}`, LogLevel.error, error);
          return error;
        }
      }
      const bodyText = fetchResponse.body ? await fetchResponse.text() : "";
      sendLog(`api: ${apiUrl} failed with ${fetchResponse.status}: ${fetchResponse.statusText}, ${bodyText}`, LogLevel.error);
      throw new Error(`Error: ${fetchResponse.status}: ${fetchResponse.statusText}`, { cause: 'ParseError' });
    }
    throw new Error(`User is no longer authenticated, please sign in again.`, { cause: 'AuthenticationError' });
  };

  const generateContent = async (draftDocument: Document, promptFlowId: number): Promise<PromptFlowExecution> => {
    const response = await callApi<PromptFlowExecution>(`ChatGpt?promptFlowId=${promptFlowId}`, HttpMethod.POST, draftDocument);
    return response;
  };

  const parseIntValue = (value: string | number | null): number => {
    if (value == null) {
      return 0;
    }
    if (typeof value === 'number') {
      return value;
    }
    return parseInt(value, 10);
  };

  const convertTopicsToResponses = (topics: GeneratedTopicModel[], sections: SiteSection[]): GeneratedTopic[] => {
    let idCounter = 1; // You can adjust this to generate real IDs if needed
    return topics.flatMap(topic =>
      topic.articles.map(article => ({
        // eslint-disable-next-line no-plusplus
        id: idCounter++,
        themeName: topic.theme,
        sectionId: sections.find(s => s.sectionName === article.categoryName)?.id,
        title: article.title,
        subTitle: article.subTitle,
        scheduledDate: new Date(article.plannedDate),
        keywordId: parseIntValue(article.keywordId),
        keyword: article.keyword,
        searchVolume: parseIntValue(article.searchVolume),
        difficulty: parseIntValue(article.difficulty),
        searchIntent: SearchIntent.Unknown,
        checked: false,
      } as GeneratedTopic))
    );
  }

  const executePromptFlow = async (request: PromptFlowExecutionRequest): Promise<PromptFlowExecution> => {
    const response = await callApi<PromptFlowExecution | PromptFlowExecution>(`ChatGpt/ExecutePromptFlow`, HttpMethod.POST, request, true);
    if (response.hasError) {
      sendLog(`Prompt flow execution failed: ${response.errorMessage}.`, LogLevel.error, response);
      notify('AI agent failed to respond in time, please retry your last action', NotificationType.Error);
      throw new Error('Prompt flow execution failed');
    }
    return response;
  }

  const triggerPromptFlowBatchJob = async (request: PromptFlowExecutionRequest): Promise<BatchJob> => {
    const response = await callApi<BatchJob>(`ChatGpt/ExecutePromptFlowAsync`, HttpMethod.POST, request);
    return response;
  }

  const checkPromptFlowExecutionStatus = async (batchJobId: string): Promise<BatchJob> => {
    const response = await callApi<BatchJob>(`ChatGpt/BatchJobs?id=${batchJobId}`, HttpMethod.GET);
    return response;
  }

  interface Concept {
    name: string;
    prerequisites?: Concept[];
  }

  const extractNames = (conceptList: Concept[]): string[] => conceptList.flatMap(concept => [
    concept.name,
    ...extractNames(concept.prerequisites || [])
  ]);

  const getSubdomainsForDomain = async (project: Project, requestId?: string): Promise<TopicModel[]> => {
    const uuid = requestId || uuidv4();

    const domainExploderRequest: PromptFlowExecutionRequest = {
      templateSlug: 'cst-domain-exploder-2',
      conversationThreadId: uuid,
      projectId: project.id!,
      inputData: {
        "Audience": project.targetAudience,
        "Topic": project.solutionForAudience,
        "Exclusions": project.globalNegativeTerms
      },
      includeCrawlerDataset: false,
      includeProjectInfo: false,
      includeSections: false,
      responseType: ResponseType.String
    }
    const domainExploderResponse = await executePromptFlow(domainExploderRequest);
    sendLog(`${uuid}: completed domain exploder request for project ${project.id}`, LogLevel.info, domainExploderResponse);

    const dagBuilderRequest: PromptFlowExecutionRequest = {
      templateSlug: 'cst-topic-authority-single-dag-builder-2',
      conversationThreadId: uuid,
      projectId: project.id!,
      inputData: {
        "Subdomains": domainExploderResponse.lastResponse,
      },
      includeCrawlerDataset: false,
      includeProjectInfo: false,
      includeSections: false,
      responseType: ResponseType.Object
    }
    const dagBuilderResponse = await executePromptFlow(dagBuilderRequest);
    sendLog(`${uuid}: completed DAG builder request for project ${project.id}`, LogLevel.info, dagBuilderResponse);
    const topicModels: TopicModel[] = JSON.parse(dagBuilderResponse.lastResponse!);
    return topicModels;
  }

  const filterSeedKeywords = async (project: Project, filteredTopics: TopicModel[], requestId?: string): Promise<string[]> => {
    const uuid = requestId || uuidv4();

    const unfilteredSeedKeywords = extractNames(filteredTopics);
    const cleanSeedKeywordsRequest: PromptFlowExecutionRequest = {
      templateSlug: 'cst-filter-topics-3',
      conversationThreadId: uuid,
      projectId: project.id!,
      inputData: {
        "Audience": project.targetAudience,
        "Topic": project.solutionForAudience,
        "Exclusions": project.globalNegativeTerms,
        "SubtopicList": unfilteredSeedKeywords,
      },
      includeCrawlerDataset: false,
      includeProjectInfo: false,
      includeSections: false,
      responseType: ResponseType.String
    }
    const cleanSeedKeywordsResponse = await executePromptFlow(cleanSeedKeywordsRequest);
    sendLog(`${uuid}: completed clean seed keywords request for project ${project.id}`, LogLevel.info, cleanSeedKeywordsResponse);

    return cleanSeedKeywordsResponse.lastResponse!.split('\n');
  }

  const filterKeywordsBySelectedSubtopics = async (project: Project, filteredTopics: TopicModel[], allKeywords: TargetKeyword[], requestId?: string): Promise<TargetKeyword[]> => {
    const uuid = requestId || uuidv4();

    const cleanKeywordsBySubtopicRequest: PromptFlowExecutionRequest = {
      templateSlug: 'cst-filter-keywords-by-topics-3',
      conversationThreadId: uuid,
      projectId: project.id!,
      inputData: {
        "Audience": project.targetAudience,
        "Topic": project.solutionForAudience,
        "Exclusions": project.globalNegativeTerms,
        "PotentialKeywords": allKeywords,
        "SubtopicList": filteredTopics,
      },
      includeCrawlerDataset: false,
      includeProjectInfo: false,
      includeSections: false,
      responseType: ResponseType.String
    }

    const cleanSeedKeywordsResponse = await executePromptFlow(cleanKeywordsBySubtopicRequest);
    sendLog(`${uuid}: completed filter keyword opportunities for project ${project.id}`, LogLevel.info, cleanSeedKeywordsResponse);

    const filteredKeywords = JSON.parse(cleanSeedKeywordsResponse.lastResponse!) as TargetKeyword[];
    return filteredKeywords;
  }

  const generateArticleIdeas = async (project: Project, topicModels: TopicModel[], seedKeywords?: string[], keywordOpportunities?: ISeoKeyword[], requestId?: string): Promise<GeneratedTopicModel[]> => {
    const uuid = requestId || uuidv4();

    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const articleGenerationRequest: PromptFlowExecutionRequest = {
      templateSlug: 'cst-topic-collapser-7',
      conversationThreadId: uuid,
      projectId: project.id!,
      inputData: {
        "Audience": project.targetAudience,
        "Topic": project.solutionForAudience,
        "Exclusions": project.globalNegativeTerms,
        "SubtopicList": topicModels,
        "NextMonth": moment(nextMonth).format('MM/DD/YYYY'),
      },
      includeCrawlerDataset: true,
      includeProjectInfo: false,
      includeSections: true,
      responseType: ResponseType.Object
    }

    if (keywordOpportunities) {
      (articleGenerationRequest.inputData as any).PotentialKeywords = keywordOpportunities;
    } else if (seedKeywords) {
      articleGenerationRequest.seedKeywords = seedKeywords;
    } else {
      throw new Error('No seed keywords or keyword opportunities provided');
    }

    const articleGenerationResponse = await executePromptFlow(articleGenerationRequest);
    sendLog(`${uuid}: completed article generation request for project ${project.id}`, LogLevel.info, articleGenerationResponse);
    const topics: GeneratedTopicModel[] = JSON.parse(articleGenerationResponse.lastResponse!);

    if (topics.length === 0) {
      sendLog(`${uuid}: no topics were generated for project ${project.id}`, LogLevel.warn);
      notify('AI agent failed to generate article ideas, please refresh the page and try again.', NotificationType.Warning);
    }

    let keywordList = [];
    if (Array.isArray(articleGenerationResponse.data.PotentialKeywords)) {
      keywordList = articleGenerationResponse.data.PotentialKeywords;
    } else {
      keywordList = JSON.parse(articleGenerationResponse.data.PotentialKeywords);
    }
    const potentialKeywords = keywordList.map((k: { SeoKeyword: string, SeoMonthlySearchVolume: number }) => ({
      SeoKeyword: k.SeoKeyword, SeoMonthlySearchVolume: k.SeoMonthlySearchVolume
    }));
    sendLog(`${uuid}: potential keywords list:`, LogLevel.info, potentialKeywords);
    const selectedKeywords = topics.flatMap(topic => topic.articles.map(article => article.keyword));
    sendLog(`${uuid}: selected keywords list:`, LogLevel.info, selectedKeywords);
    sendLog(`${uuid}: final generated topics`, LogLevel.info, topics);

    return topics;
  }

  const generateThemesClientSide = async (project: Project, sections: SiteSection[]): Promise<GeneratedTopic[]> => {
    const uuid = uuidv4();

    const topicModels = await getSubdomainsForDomain(project, uuid);
    const seedKeywords = await filterSeedKeywords(project, topicModels, uuid);
    const topics = await generateArticleIdeas(project, topicModels, seedKeywords, undefined, uuid);

    const generatedTopicsResponse = convertTopicsToResponses(topics, sections);
    sendLog(`${uuid}: converted generated topics to output format for project ${project.id}`, LogLevel.info, generatedTopicsResponse);
    return generatedTopicsResponse;
  };

  const tryGetKeywordOpportunitiesOrDefault = async (project: Project, seedTopics: TopicModel[]): Promise<TargetKeyword[]> => {
    const uuid = uuidv4();
    const maximumNumberOfAttempts = 3;
    const minumumNumberOfKeywords = 4;
    let attemptNumber = 0;
    let unfilteredKeywords: TargetKeyword[] = [];
    sendLog(`${uuid}: making first attempt at getting keyword opportunities for selected topics`, LogLevel.info, seedTopics);

    while (attemptNumber < maximumNumberOfAttempts) {
      // eslint-disable-next-line no-await-in-loop
      const filteredSeedKeywords = await filterSeedKeywords(project, seedTopics, uuid);
      const body = {
        requestId: uuid,
        projectId: project.id,
        seedKeywords: filteredSeedKeywords,
      }
      sendLog(`${uuid}: filtered seed keywords`, LogLevel.info, filteredSeedKeywords);
      // eslint-disable-next-line no-await-in-loop
      unfilteredKeywords = await callApi<TargetKeyword[]>(`ChatGpt/GetSeoKeywordOpportunities`, HttpMethod.POST, body);
      sendLog(`${uuid}: retrieved ${unfilteredKeywords.length} unfiltered keyword opportunities`, LogLevel.info, unfilteredKeywords);

      // eslint-disable-next-line no-await-in-loop
      const filteredKeywords = await filterKeywordsBySelectedSubtopics(project, seedTopics, unfilteredKeywords, uuid);

      sendLog(
        `${uuid}: filtered all keyword opportunities to fit user criteria`,
        LogLevel.info,
        {
          PotentialKeywords: unfilteredKeywords,
          SubtopicList: seedTopics,
          Results: filteredKeywords
        }
      );

      if (filteredKeywords && filteredKeywords.length >= minumumNumberOfKeywords) {
        return filteredKeywords;
      }

      attemptNumber += 1;
      sendLog(`${uuid}: starting attempt number ${attemptNumber + 1} to get keyword opportunities for selected topics`, LogLevel.warn, seedTopics);
    }
    sendLog(
      `${uuid}: failed to generate any valid keywords for selected topics`,
      LogLevel.error,
      {
        PotentialKeywords: unfilteredKeywords,
        SubtopicList: seedTopics,
        Results: []
      });
    return [];
  };

  const generateArticleThemes = async (project: Project, sections: SiteSection[]): Promise<GeneratedTopic[]> => {
    const useServer = true;
    let response: GeneratedTopic[] = [];
    if (useServer) {
      response = await callApi<GeneratedTopic[]>(`ChatGpt/GenerateTopics?projectId=${project.id}`);

    } else {
      response = await generateThemesClientSide(project, sections);
    }
    return response;
  };

  return {
    generateContent,
    generateArticleThemes,
    executePromptFlow,
    triggerPromptFlowBatchJob,
    checkPromptFlowExecutionStatus,
    getSubdomainsForDomain,
    tryGetKeywordOpportunitiesOrDefault,
    generateArticleIdeas
  } as AIGenerator;
};
